// src/components/InfoWindow/InfoWindow.tsx
/**
 * InfoWindow Component
 * Displays conversation details in a sliding panel from the right side
 * Supports multiple info windows stacked with navigation controls
 * Migrated from legacy InfoWindow.js
 */

import React, { Component, createRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import $ from 'jquery';
import RenderInBody from '@/components/Globals/RenderInBody';
import ConversationDetailsView from '@/components/ConversationDetails/ConversationDetailsView';
import type {
  InfoWindowProps,
  InfoWindowElement,
  InfoWindowWidthResult,
  OpenInfoWindowOptions,
  ReloadItemFunction,
  UpdateItemFunction,
} from './types';
import './InfoWindow.css';
import InfoWindowNavigation from './InfoWindowNavigation';
import { Modal } from '../utils/Modal';
import { getSideMenuLocal } from '../utils/CorgeeHelper';
import CorgeeStorageHelper from '@/components/Globals/CorgeeStorageHelper';
import EditCard from '../EditCard/EditCard';

import type { Conversation, SideMenu } from '@/types/dashboard';
import { normalizeConversationId } from '@lib/conversationUrl';

interface InfoWindowState {
  conversationIds: number[];
  elements: InfoWindowElement[];
  reloadItem: Record<number, ReloadItemFunction>;
  updateItem: Record<number, UpdateItemFunction>;
  currentConversationId: number | null;
  openCreateModal: boolean;
}
class InfoWindow extends Component<InfoWindowProps, InfoWindowState>
{
  private currentRecycleBin: boolean = false;
  constructor(props: InfoWindowProps)
  {
    super(props);
    this.state = {
      conversationIds: props.conversationIds || [],
      elements: [],
      reloadItem: {},
      updateItem: {},
      currentConversationId: null,
      openCreateModal: false,
    };

    this.getInfoWindowWidth = this.getInfoWindowWidth.bind(this);
    this.getConversationRequest = this.getConversationRequest.bind(this);
    this.getReloadItemFn = this.getReloadItemFn.bind(this);
    this.getUpdateItemFn = this.getUpdateItemFn.bind(this);
    this.closeInfoWindow = this.closeInfoWindow.bind(this);
    this.closeAll = this.closeAll.bind(this);
    this.updateInfoWindowsSize = this.updateInfoWindowsSize.bind(this);
    this.handleEscKey = this.handleEscKey.bind(this);
    this.openInfoWindow = this.openInfoWindow.bind(this);
    this.leftInfoWindow = this.leftInfoWindow.bind(this);
    this.rightInfoWindow = this.rightInfoWindow.bind(this);
    this.openInfoWindow = this.openInfoWindow.bind(this);
    this.leftInfoWindow = this.leftInfoWindow.bind(this);
    this.rightInfoWindow = this.rightInfoWindow.bind(this);
    this.handleCreateConversation = this.handleCreateConversation.bind(this);
    // this.renderButtons = this.renderButtons.bind(this); // Removed
  }
  editCardRef = createRef<any>();
  editCardInitRef = createRef<((conv: any, itemType: any) => void) | null>();
  private layerUpdatedDebounce: Record<number, ReturnType<typeof setTimeout>> = {};
  private pendingConversations: Record<number, any> = {};
  private _hasRegisteredBusListeners: boolean = false;
  private _skipReloadOnSwap: boolean = false;
  private resizeDebounce: ReturnType<typeof setTimeout> | null = null;

  componentDidMount(): void
  {
    if (this.props.getCloseAllFn && typeof this.props.getCloseAllFn === 'function')
    {
      this.props.getCloseAllFn(this.closeAll);
    }
    if (this.props.getCloseLastFn && typeof this.props.getCloseLastFn === 'function')
    {
      this.props.getCloseLastFn(this.closeInfoWindow);
    }
    if (this.props.getOpenInfoWindowFn && typeof this.props.getOpenInfoWindowFn === 'function')
    {
      this.props.getOpenInfoWindowFn(this.openInfoWindow);
    }

    // Prevent duplicate listener registration (React StrictMode, remounts, etc.)
    if (this._hasRegisteredBusListeners)
    {
      return;
    }

    this._hasRegisteredBusListeners = true;

    import('@/lib/conversationBus').then(({ conversationBus }) =>
    {
      this.busInstance = conversationBus as any;

      this.unsubImageUpdated = conversationBus.on('image-updated', data =>
      {
        const updateFn = this.state.updateItem[data.conversationId];
        if (updateFn)
        {
          updateFn(data.conversationId, { imageUrl: data.imageUrl });
        }
      });

      // reply-added / reply-updated are handled internally by ConversationDetailsView
      // via its own conversationBus listener (scoped to its own conversationId, with
      // an optimistic local append). Reacting again here duplicated that reload on
      // every add/edit — removed rather than left as dead weight.

      this.unsubTitleUpdated = conversationBus.on('title-updated', data =>
      {
        const updateFn = this.state.updateItem[data.conversationId];
        if (updateFn)
        {
          updateFn(data.conversationId, { Title: data.title });
        }
      });

      this.unsubRedFlagUpdated = conversationBus.on('redflag-updated', data =>
      {
        const updateFn = this.state.updateItem[data.conversationId];
        if (updateFn)
        {
          updateFn(data.conversationId, { IsRedFlag: data.isRedFlag });
        }
      });

      this.unsubImportantUpdated = conversationBus.on('important-updated', data =>
      {
        const updateFn = this.state.updateItem[data.conversationId];
        if (updateFn)
        {
          updateFn(data.conversationId, { IsImportant: data.isImportant });
        }
      });

      this.unsubConversationDeleted = conversationBus.on('conversation-deleted', data =>
      {
        if (this.state.conversationIds.includes(data.conversationId))
        {
          this.closeInfoWindowById(data.conversationId);
        }
      });

      this.unsubFieldUpdated = conversationBus.on('field-updated', data =>
      {
        const reloadFn = this.state.reloadItem[data.conversationId];
        if (reloadFn)
        {
          reloadFn(data.conversationId, '', false, false, 0, false, null, true);
        }
      });

      // Differentiated behavior based on sourceId:
      // - CustomFields (cf-*): Skip reload, updates handled locally
      // - Others (multi-edit, etc.): Full reload for sync
      this.unsubLayerUpdated = conversationBus.on('layer-updated', data =>
      {
        if (data.skipInfoWindowReload) return;

        if (this.layerUpdatedDebounce[data.conversationId])
        {
          clearTimeout(this.layerUpdatedDebounce[data.conversationId]);
        }

        // CustomFields updates locally via useConversationBus - no reload needed
        if (data.sourceId?.startsWith('cf-'))
        {
          return;
        }

        // Other sources: reload to ensure full backend sync
        this.layerUpdatedDebounce[data.conversationId] = setTimeout(() =>
        {
          delete this.layerUpdatedDebounce[data.conversationId];
          const reloadFn = this.state.reloadItem[data.conversationId];
          if (reloadFn)
          {
            reloadFn(data.conversationId, '', false, false, 0, false, null, true);
          }
        }, 0);
      });

      this.unsubLayersUpdated = conversationBus.on('layers-updated', data =>
      {
        const updateFn = this.state.updateItem[data.conversationId];
        if (updateFn)
        {
          updateFn(data.conversationId, { ConversationLayers: data.layers });
        }
      });

      this.unsubDashboardRefresh = conversationBus.on('dashboard-refresh', data =>
      {
        const reloadFn = this.state.reloadItem[data.conversationId];
        if (!reloadFn) return;

        if (data.conversationId === this.state.currentConversationId)
        {
          reloadFn(data.conversationId, '', false, false, 0, false, null, true);
        }
        else
        {
          conversationBus.emit('dashboard-refresh-body', { conversationId: data.conversationId });
        }
      });
    });

    // Listen for phase updates (when phases are added or removed from any InfoWindow)
    this.handlePhaseUpdated = (e: Event) =>
    {
      const event = e as CustomEvent<{ conversationId: string }>;
      const { conversationId } = event.detail;
      const convIdNum = Number(conversationId);

      // If this InfoWindow is showing the conversation that had phases updated, reload it
      if (this.state.conversationIds.includes(convIdNum))
      {
        const reloadFn = this.state.reloadItem[convIdNum];
        if (reloadFn)
        {
          reloadFn(convIdNum, '', false, false, 0, false, null, true);
        }
      }
    };
    window.addEventListener('corgee:conversation-phases-updated', this.handlePhaseUpdated);

    // Listen for phase creation (when phases are created from CreatePhaseModal)
    this.handlePhasesCreated = (e: Event) =>
    {
      const event = e as CustomEvent<{ conversationIds: string[]; skipInfoWindowReload?: boolean }>;
      const { conversationIds, skipInfoWindowReload } = event.detail;

      // The originating InfoWindow card already reloaded itself inline; skip the
      // redundant details-view reload to avoid a duplicate getConversationById request.
      if (skipInfoWindowReload) return;

      // Check if any of the affected conversations are currently open in InfoWindows
      conversationIds.forEach(conversationId =>
      {
        const convIdNum = Number(conversationId);
        if (this.state.conversationIds.includes(convIdNum))
        {
          const reloadFn = this.state.reloadItem[convIdNum];
          if (reloadFn)
          {
            reloadFn(convIdNum, '', false, false, 0, false, null, true);
          }
        }
      });
    };
    window.addEventListener('corgee:phases-created', this.handlePhasesCreated);

    window.addEventListener('resize', this.updateInfoWindowsSize);
    window.addEventListener('keyup', this.handleEscKey);
  }

  private unsubImageUpdated?: () => void;
  private unsubTitleUpdated?: () => void;
  private unsubRedFlagUpdated?: () => void;
  private unsubImportantUpdated?: () => void;
  private unsubConversationDeleted?: () => void;
  private unsubFieldUpdated?: () => void;
  private unsubLayerUpdated?: () => void;
  private unsubLayersUpdated?: () => void;
  private unsubDashboardRefresh?: () => void;
  private busInstance?: { emit: (event: string, data: any) => void } | null;
  private handlePhaseUpdated?: (e: Event) => void;
  private handlePhasesCreated?: (e: Event) => void;

  componentDidUpdate(_prevProps: InfoWindowProps, prevState: InfoWindowState): void
  {
    if (prevState.currentConversationId !== this.state.currentConversationId)
    {
      if (this.state.currentConversationId)
      {
        sessionStorage.setItem('C2_conversationInfoWindow', String(this.state.currentConversationId));
      }
      else
      {
        sessionStorage.removeItem('C2_conversationInfoWindow');
      }
    }
  }

  componentWillUnmount(): void
  {
    window.removeEventListener('resize', this.updateInfoWindowsSize);
    window.removeEventListener('keyup', this.handleEscKey);
    if (this.resizeDebounce)
    {
      clearTimeout(this.resizeDebounce);
      this.resizeDebounce = null;
    }
    if (this.handlePhaseUpdated)
    {
      window.removeEventListener('corgee:conversation-phases-updated', this.handlePhaseUpdated);
    }
    if (this.handlePhasesCreated)
    {
      window.removeEventListener('corgee:phases-created', this.handlePhasesCreated);
    }
    this.unsubImageUpdated?.();
    this.unsubTitleUpdated?.();
    this.unsubRedFlagUpdated?.();
    this.unsubImportantUpdated?.();
    this.unsubConversationDeleted?.();
    this.unsubFieldUpdated?.();
    this.unsubLayerUpdated?.();
    this.unsubLayersUpdated?.();
    this.unsubDashboardRefresh?.();
    Object.values(this.layerUpdatedDebounce).forEach(clearTimeout);
    this._hasRegisteredBusListeners = false;
  }

  openInfoWindow(
    conversationId: number,
    recycleBin?: boolean,
    conversation?: any,
    options?: OpenInfoWindowOptions,
  ): void
  {
    if (!conversationId) return;

    // Some callers pass the id as a string, and closeInfoWindow() compares it
    // strictly against a Number, so the window could never be closed
    conversationId = Number(conversationId);
    if (!conversationId || Number.isNaN(conversationId)) return;

    if (conversation)
    {
      this.pendingConversations[conversationId] = conversation;
    }

    // Toggle: if this conversation is already the active one, close it instead
    // With toggle disabled, an already active conversation is left as is (no close, no reload)
    if (this.state.currentConversationId === conversationId)
    {
      if (options?.toggle !== false)
      {
        this.closeInfoWindowById(conversationId);
      }
      return;
    }

    this.currentRecycleBin = recycleBin === true;

    const conversationIds = [...this.state.conversationIds];
    conversationIds.push(conversationId);

    const elements = [...this.state.elements];
    let length = conversationIds.length;

    if (length)
    {
      // Remove duplicates
      let i = length;
      while (i--)
      {
        if (conversationIds[i] === conversationId)
        {
          conversationIds.splice(i, 1);
        }
      }
      conversationIds.push(conversationId);

      length = conversationIds.length;

      const lastConversationElementInArray = $('.info-window-conversation-id-' + conversationId);

      // Is already opened?
      if (lastConversationElementInArray.length)
      {
        const lastConversationElementInView = $('.info-window-conversation-container').last();

        // If the last element in view is the selected element, reload
        if (
          lastConversationElementInView.hasClass('info-window-conversation-id-' + conversationId)
        )
        {
          // Reload
          this.state.reloadItem[conversationId]?.(
            conversationId,
            '',
            false,
            false,
            0,
            false,
            null,
            true,
          );
          this.setState({ currentConversationId: conversationId });
          // if (length > 1) this.renderButtons(conversationId); // Removed
        }
        else
        {
          // Hide elements
          const lastConversationElementInViewId = lastConversationElementInView
            .find('.info-window-item')
            .attr('id')
            ?.replace('info-window-conversationId-', '');
          let lastConversationElementInViewWidth = 0;

          for (let i = 0; i < elements.length; i++)
          {
            if (elements[i]!.conversationId === Number(lastConversationElementInViewId))
            {
              lastConversationElementInViewWidth = elements[i]!.width;
              break;
            }
          }

          const animationTime = 0;

          // Skip reload when navigating via arrows — the reload reinitialises
          // Summernote ~1 s later and wipes the in-progress reply draft.
          if (!this._skipReloadOnSwap)
          {
            this.state.reloadItem[conversationId]?.(
              conversationId,
              '',
              false,
              false,
              0,
              false,
              null,
              true,
            );
          }

          let lastConversationElementInArrayWidth = 0;
          for (let i = 0; i < elements.length; i++)
          {
            if (elements[i]!.conversationId === conversationId)
            {
              lastConversationElementInArrayWidth = elements[i]!.width;
              break;
            }
          }
          if (length > 1)
          {
            // this.renderButtons(conversationId); // Removed
          }

          // Show elements
          lastConversationElementInView.insertBefore(lastConversationElementInArray);

          const newLastConversationElementInView = $('.info-window-conversation-container').last();
          lastConversationElementInArray.insertAfter(newLastConversationElementInView);

          lastConversationElementInView.animate(
            { width: lastConversationElementInArrayWidth },
            animationTime,
            () =>
            {
              lastConversationElementInArray.animate(
                { width: lastConversationElementInViewWidth },
                animationTime,
                () =>
                {
                  for (let i = 0; i < elements.length; i++)
                  {
                    if (elements[i]!.conversationId === conversationId)
                    {
                      elements[i]!.width = lastConversationElementInViewWidth;
                      elements[i]!.element = this.createInfoWindowElement(
                        conversationId,
                        lastConversationElementInViewWidth,
                        elements[i]!.size,
                      );
                      break;
                    }
                  }

                  for (let i = 0; i < elements.length; i++)
                  {
                    if (elements[i]!.conversationId === Number(lastConversationElementInViewId))
                    {
                      elements[i]!.width = lastConversationElementInArrayWidth;
                      elements[i]!.element = this.createInfoWindowElement(
                        Number(lastConversationElementInViewId),
                        lastConversationElementInArrayWidth,
                        elements[i]!.size,
                      );
                      break;
                    }
                  }

                  this.setState({ elements, currentConversationId: conversationId });
                },
              );
            },
          );
        }
      }
      else
      {
        // Create new info window
        const getTabAndWidth = (index: number): { tab: string; objInfo: InfoWindowWidthResult } =>
        {
          if (index === 1)
          {
            const tab = 'tabA';
            return { tab, objInfo: this.getInfoWindowWidth(tab) };
          }
          else if (index === 2)
          {
            const tab = 'tabB';
            return { tab, objInfo: this.getInfoWindowWidth(tab) };
          }
          else
          {
            const tab = 'tabC';
            return { tab, objInfo: this.getInfoWindowWidth(tab) };
          }
        };

        const { objInfo } = getTabAndWidth(length);
        const width = objInfo.infoWindowWidth;
        const size = objInfo.size;

        elements.push({
          size,
          width,
          conversationId,
          element: (
            <RenderInBody
              key={conversationId}
              containerClassName={`info-window-conversation-container processing info-window-conversation-id-${conversationId} ${size}`}
              containerStyles={`width: ${width}px; right: -${width}px; z-index: 100`}
            >
              <div id={`info-window-conversationId-${conversationId}`} className="info-window-item">
                <ConversationDetailsView
                  request={this.getConversationRequest(conversationId) as any}
                />
              </div>
            </RenderInBody>
          ),
        });

        if (length === 1 && this.props.firstInfoWindowOpened)
        {
          this.props.firstInfoWindowOpened();
        }

        $('body').css({ 'overflow-x': 'hidden' });
        this.setState({ conversationIds, elements }, () =>
        {
          setTimeout(() =>
          {
            const infoWindowElement = $('.processing');

            infoWindowElement.animate({ right: 0 }, 150, () =>
            {
              for (let i = 0; i < elements.length; i++)
              {
                if (elements[i]!.conversationId === conversationId)
                {
                  elements[i]!.element = this.createInfoWindowElement(
                    conversationId,
                    elements[i]!.width,
                    elements[i]!.size,
                  );
                }
              }

              this.setState({ elements });

              infoWindowElement.removeClass('processing');

              this.setState({ elements, currentConversationId: conversationId });
              infoWindowElement.removeClass('processing');
              // if (length > 1) this.renderButtons(conversationId); // Removed
            });
          });
        });
      }
    }
  }

  createInfoWindowElement(conversationId: number, width: number, size: string): React.ReactNode
  {
    return (
      <RenderInBody
        key={conversationId}
        containerClassName={`info-window-conversation-container info-window-conversation-id-${conversationId} ${size}`}
        containerStyles={`width: ${width}px; right: 0px; z-index: 100`}
      >
        <div id={`info-window-conversationId-${conversationId}`} className="info-window-item">
          <ConversationDetailsView request={this.getConversationRequest(conversationId) as any} />
        </div>
      </RenderInBody>
    );
  }

  handleEscKey(e: KeyboardEvent): void
  {
    if (e.keyCode === 27)
    {
      const lastConversationElementInView = $('.info-window-conversation-container').last();

      e.stopPropagation();

      if (
        document.getElementsByClassName('image-gallery-component').length > 0 ||
        document.getElementsByClassName('corgee_com_image-gallery-modal-footer').length > 0 ||
        document.getElementsByClassName('corgee_com_conversation-metadata-modal-video').length > 0
      )
      {
        return;
      }

      // If the focused element is inside a Summernote editor, let the editor handle ESC (close editor, not InfoWindow)
      const activeEl = document.activeElement;
      if (
        activeEl &&
        (activeEl.classList.contains('note-editable') ||
          activeEl.closest('.note-editable') !== null)
      )
      {
        return;
      }

      // Legacy summernote (panel-body class)
      if (lastConversationElementInView.find('.note-editable.panel-body').length > 0)
      {
        lastConversationElementInView.find('.note-editable.panel-body').trigger('focus');
        return;
      }
      else if (
        document.getElementsByClassName('dialogTime').length > 0 ||
        document.getElementsByClassName('preview-image-pasted').length > 0
      )
      {
        return;
      }

      this.closeInfoWindow();
    }
  }

  closeInfoWindow(): void
  {
    const lastConversationElementInView = $('.info-window-conversation-container').last();
    const lastConversationElementInViewId = lastConversationElementInView
      .find('.info-window-item')
      .attr('id');
    const conversationId = Number(
      lastConversationElementInViewId?.replace('info-window-conversationId-', ''),
    );
    if (!conversationId || Number.isNaN(conversationId))
    {
      return;
    }

    const conversationIds = [...this.state.conversationIds];
    const elements = [...this.state.elements];
    let width = 0;

    for (let i = 0; i < conversationIds.length; i++)
    {
      if (conversationIds[i] === conversationId)
      {
        conversationIds.splice(i, 1);
        break;
      }
    }

    for (let i = 0; i < elements.length; i++)
    {
      if (elements[i]!.conversationId === conversationId)
      {
        width = elements[i]!.width;
        elements.splice(i, 1);
        break;
      }
    }

    if (conversationIds.length === 0 || elements.length === 0)
    {
      if (this.props.lastInfoWindowOpened)
      {
        this.props.lastInfoWindowOpened();
      }
    }

    // Fall back to the rendered width: after a browser resize the width kept in
    // state may not match the element on screen.
    if (!width)
    {
      width = lastConversationElementInView.outerWidth() || 0;
    }

    let nextConversationId: number | null = null;
    if (conversationIds.length > 0)
    {
      nextConversationId = conversationIds[conversationIds.length - 1] ?? null;
    }

    // The state update must not depend on the animation callback: any pending
    // animation in the fx queue (e.g. from resizing) would otherwise delay or
    // swallow it and the window would stay open.
    let closed = false;
    const applyClose = () =>
    {
      if (closed) return;
      closed = true;
      this.setState({ conversationIds, elements, currentConversationId: nextConversationId });
    };

    lastConversationElementInView
      .stop(true, false)
      .animate({ right: '-' + width + 'px' }, 150, applyClose);

    // Safety net in case the animation never completes (element detached, tab hidden).
    setTimeout(applyClose, 300);
  }

  closeAll(): void
  {
    // const infoWindowElements = $('.info-window-conversation-container');

    // infoWindowElements.each((_index, e) => {
    //   const infoWindowElement = $(e);
    //   const width = infoWindowElement.width() || 0;

    this.setState({ conversationIds: [], elements: [], currentConversationId: null });
    // });

    if (this.props.lastInfoWindowOpened)
    {
      this.props.lastInfoWindowOpened();
    }
  }

  closeInfoWindowById(conversationId: number): void
  {
    conversationId = Number(conversationId);
    const elements = this.state.elements.filter(e => e.conversationId !== conversationId);
    const conversationIds = this.state.conversationIds.filter(id => id !== conversationId);

    if (conversationIds.length === 0 && this.props.lastInfoWindowOpened)
    {
      this.props.lastInfoWindowOpened();
    }

    const nextConversationId =
      conversationIds.length > 0 ? (conversationIds[conversationIds.length - 1] ?? null) : null;

    this.setState({ conversationIds, elements, currentConversationId: nextConversationId });
  }

  /**
   * Debounced resize entry point.
   * A browser resize fires dozens of events; without debouncing, each one queued a
   * 400ms jQuery animation on the same fx queue. The close animation is appended to
   * that same queue, so its completion callback (which removes the window from state)
   * was delayed for seconds and the InfoWindow appeared impossible to close.
   */
  updateInfoWindowsSize(): void
  {
    if (this.resizeDebounce)
    {
      clearTimeout(this.resizeDebounce);
    }

    this.resizeDebounce = setTimeout(() =>
    {
      this.resizeDebounce = null;
      this.applyInfoWindowsSize();
    }, 150);
  }

  applyInfoWindowsSize(): void
  {
    const infoWindowElements = $('.info-window-conversation-container');
    const elements = this.state.elements;

    infoWindowElements.each((index, e) =>
    {
      const infoWindowElement = $(e);
      let objInfo: InfoWindowWidthResult;

      if (index === 0)
      {
        objInfo = this.getInfoWindowWidth('tabA');
      }
      else if (index === 1)
      {
        objInfo = this.getInfoWindowWidth('tabB');
      }
      else
      {
        objInfo = this.getInfoWindowWidth('tabC');
      }

      const width = objInfo.infoWindowWidth;
      const size = objInfo.size;

      const isHasControls = $('.fake-buttons');
      if (isHasControls.length > 0 && $('.fake-buttons')[0])
      {
        ($('.fake-buttons')[0] as HTMLElement).style.width = width + 'px';
      }

      infoWindowElement.removeClass('info_min').removeClass('info_medium').removeClass('info_max');
      infoWindowElement.addClass(size);
      // Drop any pending animation on this element before queueing a new one,
      // so the fx queue never grows across repeated resizes.
      infoWindowElement.stop(true, false).animate({ width: width + 'px' }, 400);

      // Keep state in sync with the new width/size, otherwise closeInfoWindow()
      // animates the panel out using the pre-resize width.
      const conversationId = Number(
        infoWindowElement.find('.info-window-item').attr('id')?.replace('info-window-conversationId-', ''),
      );
      const stateElement = elements.find(el => el.conversationId === conversationId);
      if (stateElement)
      {
        // Mutated in place on purpose: re-rendering here would recreate the
        // RenderInBody containers and undo the width animation above.
        stateElement.width = width;
        stateElement.size = size;
      }
    });
  }

  getInfoWindowWidth(infoWindowTab: string): InfoWindowWidthResult
  {
    const screenWidth = window.outerWidth;
    const objInfo: InfoWindowWidthResult = {
      infoWindowWidth: 0,
      size: 'info_min',
    };

    if (screenWidth >= 1440 && screenWidth < 1920)
    {
      objInfo.size = 'info_medium';
      switch (infoWindowTab)
      {
        case 'tabA':
          objInfo.infoWindowWidth = 632;
          break;
        case 'tabB':
          objInfo.infoWindowWidth = 616;
          break;
        case 'tabC':
          objInfo.infoWindowWidth = 600;
          break;
        default:
          break;
      }
    }
    else if (screenWidth >= 1920)
    {
      objInfo.size = 'info_max';
      switch (infoWindowTab)
      {
        case 'tabA':
          objInfo.infoWindowWidth = 640;
          break;
        case 'tabB':
          objInfo.infoWindowWidth = 624;
          break;
        case 'tabC':
          objInfo.infoWindowWidth = 608;
          break;
        default:
          break;
      }
    }
    else
    {
      objInfo.size = 'info_min';
      switch (infoWindowTab)
      {
        case 'tabA':
          objInfo.infoWindowWidth = 512;
          break;
        case 'tabB':
          objInfo.infoWindowWidth = 496;
          break;
        case 'tabC':
          objInfo.infoWindowWidth = 480;
          break;
        default:
          break;
      }
    }
    return objInfo;
  }

  getConversationRequest(conversationId: number): any
  {
    const {
      api,
      config,
      storageData,
      functions,
      realItemTypes,
      userData,
      socket,
      openChat,
      sendToBookmarks,
      onOpenNewWindow,
    } = this.props;
    const self = this;

    const pendingConversation = this.pendingConversations[conversationId];
    if (pendingConversation)
    {
      delete this.pendingConversations[conversationId];
    }

    return {
      enableLoading: true,
      showErrorMessage: true,
      api: api,
      isInfoWindow: true,
      isInfoWindowChat: true,
      isInfowindowViewCard: true,
      isInfowindowDrag: true,
      hasArchivedFilter: false,
      isConversationDetailsView: true,
      RecycleBin: this.currentRecycleBin,
      HasItemCreationPermission: this.props.HasItemCreationPermission ?? localStorage.getItem('C2_hasItemCreationPermission') === 'true',
      config: config,
      storageData: storageData,
      userData: userData,
      socketConnection: socket,
      conversation: pendingConversation,
      params: {
        ConversationId: conversationId,
        SocketConnectionId: socket?.id || null,
        IsFromProject: false,
        ShowMoveTo: true,
        DashboardGroupId: pendingConversation?.DashboardGroupId,
        ConfigMenus: {
          conversation: [
            'goto-project',
            'close-infowindow',
            'opendetails-newtab',
            'mark-redflag',
            'mark-important',
            'follow-conv',
            'create-itemtype',
            'convert-to',
            'duplicate-project',
            'copylink-to-clipboard',
            'move-to',
            'copy-to',
            'submit-time',
            'edit-history',
            'edit',
            'delete',
            'duplicate-item',
            'archive',
          ],
          reply: ['mark-favorite', 'mark-important', 'repost', 'edit', 'delete', 'create-itemtype'],
        },
      },
      functions: {
        onLoadDataCallback: function () {},
        onSendError: function (convId: number)
        {
          $('#closeIcon-' + convId).show();
        },
        loadingBarStart: function () {},
        loadingBarComplete: function () {},
        openInfoWindow: function (conv: any)
        {
          // Callers may pass a number, a DOM attribute string or a { ConversationId }
          const id = normalizeConversationId(conv);
          if (id) self.openInfoWindow(id);
        },
        attachReactFunctions: function () {},
        onUpdateConversation: function (convId: number, updates: Record<string, any>)
        {
          const bus = self.busInstance;
          if (!bus) return;
          if (updates.Title !== undefined)
            bus.emit('title-updated', { conversationId: convId, title: updates.Title });
          if (updates.IsRedFlag !== undefined)
            bus.emit('redflag-updated', { conversationId: convId, isRedFlag: updates.IsRedFlag });
          if (updates.IsImportant !== undefined)
            bus.emit('important-updated', {
              conversationId: convId,
              isImportant: updates.IsImportant,
            });
          if (updates.ConversationLayers !== undefined)
            bus.emit('layers-updated', {
              conversationId: convId,
              layers: updates.ConversationLayers,
            });
          if (updates.IsEnable === false)
            bus.emit('conversation-deleted', { conversationId: convId });
        },
        onAddReply: function () {},
        onUpdateReply: function () {},
        // Left undefined when the host has no chat handler so cards open the chat themselves
        openChat: openChat
          ? function (personId: number)
          {
            openChat(personId);
          }
          : undefined,
        sendToBookmarks: function (conversation: any)
        {
          if (sendToBookmarks)
          {
            sendToBookmarks(conversation);
          }
        },
        onDuplicateItem: function () {},
        addLayerToCard:
          functions?.addLayerToCard && typeof functions.addLayerToCard === 'function'
            ? functions.addLayerToCard
            : function () {},
        onClickParentTasks:
          functions?.onClickParentTasks && typeof functions.onClickParentTasks === 'function'
            ? functions.onClickParentTasks
            : function (
              _e: React.MouseEvent | MouseEvent,
              conversationId: number,
              closeModalCb?: () => void,
            )
            {
              // Open the parent card in this info window stack
              const id = normalizeConversationId(conversationId);
              if (typeof closeModalCb === 'function')
              {
                closeModalCb();
              }
              if (id) self.openInfoWindow(id);
            },
        onHoverProfilePicture:
          functions?.onHoverProfilePicture && typeof functions.onHoverProfilePicture === 'function'
            ? functions.onHoverProfilePicture
            : undefined,
        onLeaveProfilePicture:
          functions?.onLeaveProfilePicture && typeof functions.onLeaveProfilePicture === 'function'
            ? functions.onLeaveProfilePicture
            : undefined,
        getTaskUsers: function () {},
        getTaskDashboards: function () {},
        getTaskParent: function () {},
        openFavoritesModal: function () {},
        goToProject:
          functions?.goToProject && typeof functions.goToProject === 'function'
            ? functions.goToProject
            : function (e: React.MouseEvent | MouseEvent, conversation: any)
            {
              const path = `/ProjectDetails/${conversation.ProjectId}`;
              if (e && (e.shiftKey || e.ctrlKey || e.metaKey))
              {
                window.open(window.location.origin + path, '_blank');
              }
              else if (self.props.navigate)
              {
                self.props.navigate(path);
              }
            },
        goToDashboard: function () {},
        editConversation: function () {},
        deleteConversation: function (_e: any, conv: any)
        {
          const convId = conv?.ConversationId;
          if (!convId) return;
          const bus = self.busInstance;
          if (bus) bus.emit('conversation-deleted', { conversationId: convId });
        },
        onOpenGallery: function () {},
        onFileDownload: function () {},
        onCopyToClipboard: function () {},
        getBaseUrl:
          functions?.getBaseUrl && typeof functions.getBaseUrl === 'function'
            ? functions.getBaseUrl
            : undefined,
        editReply: function () {},
        deleteReply: function () {},
        archiveConversation: function () {},
        destroy: function () {},
        saveSearchResult: function () {},
        copyTo: function () {},
        onReplyDeleted: function () {},
        onCreateItemFromCustomType: function (item: any, conversation: any)
        {
          const itemType = {
            ...item,
            ConfigValueId: item.ConfigValueID,
            RelatedReplyId: 0,
          } as SideMenu;
          self.handleCreateConversation(itemType, {
            ConversationId: conversation?.ConversationId,
            ProjectId: conversation?.ProjectId,
            DashboardGroupId: conversation?.DashboardGroupId,
          });
        },
        onItemConvertTo: function () {},
        onChangedProject: function () {},
        onChangedSecurityGroups: function () {},
        onChangedGroupLayers: function () {},
        onChangeLayers: function () {},
        onCloseInfoWindow: function ()
        {
          self.closeInfoWindow();
        },
        getReloadItemFn: function (updateItemFn: ReloadItemFunction, convId: number)
        {
          self.getReloadItemFn(updateItemFn, convId);
        },
        getUpdateItemFn: function (updateFn: UpdateItemFunction, convId: number)
        {
          self.getUpdateItemFn(updateFn, convId);
        },
        onOpenNewWindow: function (conversation: any)
        {
          if (onOpenNewWindow)
          {
            onOpenNewWindow(conversation);
          }
          else
          {
            window.open(
              window.location.origin +
                import.meta.env.BASE_URL +
                'Dashboard/ConversationDetails/#/' +
                conversation.ConversationId,
              '_blank',
            );
          }
        },
        closeNotifications:
          functions?.closeNotifications && typeof functions.closeNotifications === 'function'
            ? functions.closeNotifications
            : undefined,
        UpdateCustomFields: function (fields: any, conversation: any, updatedField: any)
        {
          if (self.props.functions?.UpdateCustomFields)
          {
            self.props.functions.UpdateCustomFields(fields, conversation, updatedField);
          }
        },
      },
      realItemTypes: realItemTypes,
    };
  }

  getReloadItemFn(fn: ReloadItemFunction, conversationId: number): void
  {
    const reloadItem = { ...this.state.reloadItem };
    reloadItem[conversationId] = fn;
    this.setState({ reloadItem });
  }

  getUpdateItemFn(fn: UpdateItemFunction, conversationId: number): void
  {
    const updateItem = { ...this.state.updateItem };
    updateItem[conversationId] = fn;
    this.setState({ updateItem });
  }

  leftInfoWindow(): void
  {
    const conversationId = this.state.currentConversationId;
    if (!conversationId) return;

    let index = -1;
    const conversationIds = [...this.state.conversationIds];

    for (let i = 0; i < conversationIds.length; i++)
    {
      if (conversationIds[i] === conversationId)
      {
        index = i;
        break;
      }
    }

    const prevId = conversationIds[index - 1];
    if (index !== 0 && prevId !== undefined)
    {
      this._skipReloadOnSwap = true;
      this.openInfoWindow(prevId);
      this._skipReloadOnSwap = false;
    }
  }

  rightInfoWindow(): void
  {
    const conversationId = this.state.currentConversationId;
    if (!conversationId) return;

    let index = -1;
    const conversationIds = [...this.state.conversationIds];

    for (let i = 0; i < conversationIds.length; i++)
    {
      if (conversationIds[i] === conversationId)
      {
        index = i;
        break;
      }
    }

    const nextId = conversationIds[index + 1];
    if (conversationIds.length - 1 !== index && nextId !== undefined)
    {
      this._skipReloadOnSwap = true;
      this.openInfoWindow(nextId);
      this._skipReloadOnSwap = false;
    }
  }

  handleCreateConversation(
    itemType: SideMenu,
    sourceConversation?: { ConversationId?: number; ProjectId?: number; DashboardGroupId?: number },
  ): void
  {
    const conv: Conversation = {} as Conversation;
    if (sourceConversation)
    {
      conv.ProjectId = sourceConversation.ProjectId || 0;
      (conv as any).ConversationId = sourceConversation.ConversationId;
      (conv as any).DashboardGroupId = sourceConversation.DashboardGroupId;
    }
    else if (location.href.indexOf('ProjectDetails') > -1)
    {
      conv.ProjectId = parseInt(
        window.location.pathname.split('ProjectDetails')[1]?.split('/')[1] || '0',
      );
    }
    this.editCardInitRef.current?.(conv, itemType);
    this.editCardRef.current?.openModal();
    this.setState({ openCreateModal: false });
  }

  render(): React.ReactNode
  {
    const { elements, conversationIds, currentConversationId, openCreateModal } = this.state;
    const { api, socket } = this.props;
    const currentIndex = conversationIds.findIndex(id => id === currentConversationId);

    // Calculate width for navigation controls
    let currentWidth = 0;
    if (elements.length > 0 && currentConversationId)
    {
      const currentElement = elements.find(e => e.conversationId === currentConversationId);
      if (currentElement)
      {
        currentWidth = currentElement.width;
      }
    }

    const sideMenu = getSideMenuLocal();
    const hasItemCreationPermission =
      localStorage.getItem('C2_hasItemCreationPermission') === 'true';
    const addUserItem = sideMenu?.find(x => x.ConfigValueID === 15);
    // The "+" add button only makes sense on the Group Dashboards and Project views,
    // not on the standalone ConversationDetails page (no dashboard column to add to).
    const addNewButtonAllowedRoutes = [/^\/Dashboards(\/|$)/, /^\/ProjectDetails(\/|$)/];
    const isAddNewButtonRoute = addNewButtonAllowedRoutes.some(pattern =>
      pattern.test(this.props.pathname || ''));

    return (
      <div className="info-window-component">
        {elements.map(item =>
        {
          return item.element;
        })}
        {conversationIds.length > 1 && (
          <InfoWindowNavigation
            isVisible={true}
            width={currentWidth}
            onLeftClick={this.leftInfoWindow}
            onRightClick={this.rightInfoWindow}
            onCloseAllClick={this.closeAll}
            isLeftDisabled={currentIndex <= 0}
            isRightDisabled={currentIndex >= conversationIds.length - 1}
          />
        )}
        {hasItemCreationPermission && isAddNewButtonRoute && (
          <div
            id="btnAddNew"
            className="fixed-new-item-button dark:!bg-[#3c6c80]"
            style={{
              right:
                conversationIds.length > 0
                  ? this.getInfoWindowWidth('tabA').infoWindowWidth + 48
                  : 48,
            }}
            onClick={(e: React.MouseEvent) =>
            {
              e.preventDefault();
              this.setState({ openCreateModal: true });
            }}
          >
            <a className="icon-plus-icon" href="" ng-click="openDialog($event);"></a>
          </div>
        )}
        <Modal
          open={openCreateModal}
          onClose={() =>
          {
            this.setState({ openCreateModal: false });
          }}
          preventCloseClickOut
          title="I want to create a"
          className="max-w-[80%] w-[400px] !h-96 flex justify-evenly px-0 "
          classNameTitle="px-[25px]"
        >
          <div className="create-new">
            {sideMenu?.map((item, _index) =>
            {
              if (item.ConfigValueID === 1 || item.ConfigValueID === 15) return null;
              return (
                <div key={item.ConfigValueID} className="dialogNewItem">
                  <div
                    className="new-item"
                    onClick={e =>
                    {
                      e.preventDefault();
                      this.handleCreateConversation(item);
                    }}
                  >
                    <div className="item-header">
                      <a href="" className={item.Icon}></a>
                    </div>
                    <div className="item-text">
                      <a href="">{item.Text}</a>
                    </div>
                  </div>
                </div>
              );
            })}
            {CorgeeStorageHelper.isAdmin() && (
              <div className="inner dialogNewItem">
                <div
                  className="new-item"
                  onClick={e =>
                  {
                    e.stopPropagation();
                    this.props.navigate?.('/AddPeople');
                    this.setState({ openCreateModal: false });
                  }}
                >
                  <div className="item-header">
                    <a className="material-icons" style={{ fontSize: '40px', cursor: 'pointer' }}>
                      person_add
                    </a>
                  </div>
                  <div className="item-text">
                    <a>Add {addUserItem?.Text}</a>
                  </div>
                </div>
              </div>
            )}
          </div>
        </Modal>

        <EditCard
          ref={this.editCardRef}
          config={{
            apiUrl: api.url,
            authToken: api.token,
            SocketConnectionId: socket,
            attachReactFunctions: (fns: any) =>
            {
              this.editCardInitRef.current = fns.initConfig;
            },
            functions: {
              loadingBarStart: () => {},
              loadingBarComplete: () => {},
              conversationResponse: (_payload: any, data: any) =>
              {
                if (data?.Conversation.ConversationId)
                {
                  this.props.functionsLoad?.();
                  // To keep it simple, background refresh everything when a card is edited
                  //loadData(true);
                }
              },
            },
            params: { Conversation: {} },
          }}
          UpdateCustomFields={() => {}}
        />
      </div>
    );
  }
}

const InfoWindowWithNavigate: React.FC<InfoWindowProps> = props =>
{
  const navigate = useNavigate();
  const location = useLocation();
  return <InfoWindow {...props} navigate={navigate} pathname={location.pathname} />;
};

export default InfoWindowWithNavigate;
